import {useEffect, useState} from 'react'
import {Modal, Button, Form} from 'react-bootstrap'
import {useMutation} from '@apollo/client'
import {toast} from 'react-toastify'

import {UPDATE_KANBAN_STAGE} from '../../../gql/mutations/kanban'
import {IUpdateKanbanStageRes, IKanbanStage} from '../../../types/kanban'

interface Props {
  show: boolean
  stage?: IKanbanStage
  onClose: () => void
  onUpdated: () => void
}

export const ModalEditKanbanStage: React.FC<Props> = ({show, stage, onClose, onUpdated}) => {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [color, setColor] = useState<string>('')
  const [isClosedState, setIsClosedState] = useState(false)

  useEffect(() => {
    if (stage && show) {
      setName(stage.name)
      setDescription(stage.description || '')
      setColor(stage.color || '')
      setIsClosedState(!!stage.isClosedState)
    }
  }, [stage, show])

  const [updateStage, {loading}] = useMutation<IUpdateKanbanStageRes>(UPDATE_KANBAN_STAGE)

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!stage?.id) return
    try {
      const {data} = await updateStage({
        variables: {
          id: stage.id,
          name,
          description: description || null,
          color: color || null,
          isClosedState,
        },
      })
      if (!data?.updateKanbanStage?.ok) {
        toast.error(data?.updateKanbanStage?.errors?.[0] || 'Error updating the column')
        return
      }
      toast.success('Column updated successfully')
      onUpdated()
      onClose()
    } catch (error) {
      console.error(error)
      toast.error('Error updating the column')
    }
  }

  return (
    <Modal show={show} onHide={onClose} centered className='kanban-stage-modal'>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton className='kanban-stage-modal__header'>
          <Modal.Title>Edit column</Modal.Title>
        </Modal.Header>
        <Modal.Body className='kanban-stage-modal__body'>
          <Form.Group className='kanban-stage-modal__group'>
            <Form.Label>Name</Form.Label>
            <Form.Control
              className='kanban-stage-modal__input'
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </Form.Group>
          <Form.Group className='kanban-stage-modal__group'>
            <Form.Label>Description</Form.Label>
            <Form.Control
              className='kanban-stage-modal__input'
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Form.Group>
          <Form.Group className='kanban-stage-modal__group'>
            <Form.Label>Color</Form.Label>
            <div className='kanban-color-picker'>
              <Form.Control
                className='kanban-stage-modal__input kanban-stage-modal__input--color'
                type='color'
                value={color || '#ffffff'}
                onChange={(e) => setColor(e.target.value)}
              />
              <Button variant='link' size='sm' onClick={() => setColor('')}>
                Sin color
              </Button>
            </div>
          </Form.Group>
          <Form.Check
            className='kanban-stage-modal__checkbox'
            type='switch'
            id='kanban-stage-edit-closed'
            label='Is Closed State?'
            checked={isClosedState}
            onChange={(e) => setIsClosedState(e.target.checked)}
          />
        </Modal.Body>
        <Modal.Footer className='kanban-stage-modal__footer'>
          <Button variant='light' onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant='primary' type='submit' disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}
